import React from 'react';

import classNames from 'classnames';

import { formatActivityDate, formatActivityDateTime, orgRoleLabel } from './organisationUserFormat';
import { CountBadge, MfaPill, StatusPill } from './OrganisationUserPills';
import OrganisationUserRowActions from './OrganisationUserRowActions';

import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

// The shared table draws a divider between every cell and fills rows grey;
// this table keeps only the row dividers on white rows, as designed. Both
// rules are more specific than a plain utility, hence the `!`.
const CELL = 'tw-text-start !tw-border-r-0';
const ROW = '!tw-bg-white';

const COLUMNS = () => [
    { key: 'name', label: $t('Name') },
    { key: 'email', label: $t('Email') },
    { key: 'org_role', label: $t('Org role') },
    { key: 'projects', label: $t('Projects') },
    { key: 'invite_sent', label: $t('Invite sent') },
    { key: 'invite_accepted', label: $t('Invite accepted') },
    { key: 'last_login', label: $t('Last login') },
    { key: 'logins', label: $t('Logins') },
    { key: 'mfa', label: $t('MFA') },
    { key: 'status', label: $t('Status') },
];

function Muted({ children }) {
    return <span className="tw-text-charcoal-50">{children}</span>;
}

function DateCell({ value, empty, withTime = false }) {
    if (!value) {
        return <Muted>{empty}</Muted>;
    }

    return withTime ? formatActivityDateTime(value) : formatActivityDate(value);
}

function SortHeader({ column, sort, direction, onSort }) {
    const active = sort === column.key;

    return (
        <th className="!tw-text-start" aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button type="button" className="tw-font-semibold" onClick={() => onSort(column.key, active && direction === 'asc' ? 'desc' : 'asc')}>
                <TableSortHeaderCell asc={active && direction === 'asc'} desc={active && direction === 'desc'} hideInactiveIcon iconClassName="tw-text-blue">
                    {column.label}
                </TableSortHeaderCell>
            </button>
        </th>
    );
}

function OrganisationUserRow({ row, canManage, currentUserId, onOpen, actions }) {
    return (
        <tr className={classNames(ROW, 'tw-cursor-pointer hover:!tw-bg-blue-10')} onClick={() => onOpen(row)}>
            <td className={classNames(CELL, 'tw-font-medium')}>{row.name}</td>
            <td className={CELL}>{row.email}</td>
            <td className={CELL}>{orgRoleLabel(row.org_role)}</td>
            <td className={CELL}>
                <CountBadge value={row.projects_count} />
            </td>
            <td className={CELL}>
                <DateCell value={row.invite_sent_at} empty={$t('Not recorded')} />
            </td>
            <td className={CELL}>
                <DateCell value={row.invite_accepted_at} empty={$t('Not yet')} />
            </td>
            <td className={CELL}>
                <DateCell value={row.last_login_at} empty={$t('Never')} withTime />
            </td>
            <td className={CELL}>{row.logins_count}</td>
            <td className={CELL}>
                <MfaPill setUp={row.mfa_set_up} />
            </td>
            <td className={CELL}>
                <StatusPill status={row.status} />
            </td>
            <td className={CELL}>
                <OrganisationUserRowActions row={row} canManage={canManage} isSelf={row.id === currentUserId} {...actions} />
            </td>
        </tr>
    );
}

export default function OrganisationUserTable({ rows, sort, direction, canManage, currentUserId, onSort, onOpen, actions }) {
    const columns = COLUMNS();

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <SortHeader key={column.key} column={column} sort={sort} direction={direction} onSort={onSort} />
                        ))}
                        <th className="!tw-text-start">{$t('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr className={ROW}>
                            <td colSpan={columns.length + 1} className={classNames(CELL, '!tw-text-center tw-text-charcoal-75')}>
                                {$t('No users match these filters.')}
                            </td>
                        </tr>
                    )}
                    {rows.map((row) => (
                        <OrganisationUserRow key={row.id} row={row} canManage={canManage} currentUserId={currentUserId} onOpen={onOpen} actions={actions} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}
